import { useState, useEffect } from 'react';
import { User, Mail, Save, Loader2, CheckCircle2, XCircle, Shield, Eye, EyeOff, Building2, BadgeCheck, KeyRound, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  direction: 'Direction',
  responsable: 'Responsable',
  user: 'Utilisateur',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  direction: 'bg-amber-100 text-amber-700',
  responsable: 'bg-blue-100 text-blue-700',
  user: 'bg-emerald-100 text-emerald-700',
};

export default function Profile() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const [userForm, setUserForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setUserForm({
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (userForm.newPassword) {
        if (userForm.newPassword !== userForm.confirmPassword) {
          throw new Error('Les nouveaux mots de passe ne correspondent pas');
        }
        if (userForm.newPassword.length < 6) {
          throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères');
        }
        if (!userForm.currentPassword) {
          throw new Error('Veuillez saisir votre mot de passe actuel');
        }

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('password')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;

        const isValidPassword = await bcrypt.compare(userForm.currentPassword, userData.password);
        if (!isValidPassword) {
          throw new Error('Mot de passe actuel incorrect');
        }
      }

      let updateData: any = {
        firstname: userForm.firstname.trim(),
        lastname: userForm.lastname.trim(),
        email: userForm.email.trim()
      };

      if (userForm.newPassword) {
        const salt = await bcrypt.genSalt(10);
        updateData.password = await bcrypt.hash(userForm.newPassword, salt);
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess('Profil mis à jour avec succès');
      setUserForm(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  const initials = `${user?.firstname?.[0] ?? ''}${user?.lastname?.[0] ?? ''}`.toUpperCase();
  const agencies = (user?.agency || '').split(',').map(a => a.trim()).filter(Boolean);
  const roleLabel = ROLE_LABELS[user?.role ?? ''] ?? user?.role ?? '';
  const roleColor = ROLE_COLORS[user?.role ?? ''] ?? 'bg-slate-100 text-slate-600';

  const inputClass = "w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors bg-white";
  const readonlyClass = "w-full px-3 py-2.5 border border-slate-100 rounded-xl text-sm text-slate-500 bg-slate-50 cursor-default select-none";
  const labelClass = "block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide";

  return (
    <div className="space-y-6 max-w-4xl">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row items-center sm:items-end gap-5 px-7 py-7">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 0 24px rgba(99,102,241,0.45)' }}
            >
              {initials}
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-slate-900" />
          </div>

          {/* Infos */}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold text-white tracking-tight">
              {user?.firstname} {user?.lastname}
            </h1>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleColor}`}>
                {roleLabel}
              </span>
              {agencies.map(a => (
                <span key={a} className="flex items-center gap-1 px-2.5 py-0.5 bg-white/10 text-white/80 rounded-full text-xs font-medium">
                  <Building2 size={10} />
                  {a}
                </span>
              ))}
            </div>
            <p className="text-sm text-slate-400 mt-1.5">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* ── Feedback messages ── */}
      {error && (
        <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          <XCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 text-sm">
          <CheckCircle2 size={16} className="shrink-0" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Informations personnelles ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <User size={15} className="text-indigo-600" />
            </div>
            <h2 className="text-sm font-bold text-slate-700">Informations personnelles</h2>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Prénom</label>
                <input
                  type="text"
                  value={userForm.firstname}
                  onChange={e => setUserForm(p => ({ ...p, firstname: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Nom</label>
                <input
                  type="text"
                  value={userForm.lastname}
                  onChange={e => setUserForm(p => ({ ...p, lastname: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={userForm.email}
                onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Agence(s)</label>
              {agencies.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 px-3 py-2.5 border border-slate-100 rounded-xl bg-slate-50 min-h-[42px]">
                  {agencies.map(a => (
                    <span key={a} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-md text-xs font-medium">
                      {a}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={readonlyClass}>—</div>
              )}
            </div>

            <div>
              <label className={labelClass}>Rôle</label>
              <div className={readonlyClass + ' flex items-center gap-2'}>
                <BadgeCheck size={14} className="text-slate-400 shrink-0" />
                {roleLabel}
              </div>
            </div>
          </div>
        </div>

        {/* ── Colonne droite ── */}
        <div className="flex flex-col gap-6">

          {/* Sécurité */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <Shield size={15} className="text-violet-600" />
              </div>
              <h2 className="text-sm font-bold text-slate-700">Sécurité</h2>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className={labelClass}>Mot de passe actuel</label>
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={userForm.currentPassword}
                    onChange={e => setUserForm(p => ({ ...p, currentPassword: e.target.value }))}
                    className={inputClass + ' pr-10'}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelClass}>Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={userForm.newPassword}
                    onChange={e => setUserForm(p => ({ ...p, newPassword: e.target.value }))}
                    className={inputClass + ' pr-10'}
                    placeholder="Nouveau mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(v => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelClass}>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={userForm.confirmPassword}
                  onChange={e => setUserForm(p => ({ ...p, confirmPassword: e.target.value }))}
                  className={inputClass}
                  placeholder="Confirmer le mot de passe"
                />
              </div>

              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <Shield size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Laissez les champs vides si vous ne souhaitez pas modifier votre mot de passe.
                </p>
              </div>
            </div>
          </div>

          {/* Informations du compte */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <BadgeCheck size={15} className="text-emerald-600" />
              </div>
              <h2 className="text-sm font-bold text-slate-700">Informations du compte</h2>
            </div>

            <div className="p-5 space-y-3">

              {/* Statut */}
              <div className="flex items-center justify-between py-2.5 border-b border-slate-50">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Statut</span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                  Actif
                </span>
              </div>

              {/* Rôle */}
              <div className="flex items-center justify-between py-2.5 border-b border-slate-50">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Rôle</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleColor}`}>
                  {roleLabel}
                </span>
              </div>

              {/* Agences */}
              <div className="flex items-start justify-between py-2.5 border-b border-slate-50 gap-4">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide shrink-0">Agences</span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {agencies.length > 0 ? agencies.map(a => (
                    <span key={a} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium">
                      {a}
                    </span>
                  )) : <span className="text-sm text-slate-400">—</span>}
                </div>
              </div>

              {/* Mot de passe */}
              <div className="flex items-center justify-between py-2.5">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Mot de passe</span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500">
                  <KeyRound size={13} className="text-slate-400" />
                  Chiffré (bcrypt)
                </span>
              </div>

              {/* Info admin */}
              <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 mt-1">
                <Info size={13} className="text-indigo-400 shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-600 leading-relaxed">
                  Le rôle et les agences sont gérés par un administrateur.
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* ── Note notifications + Save ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3.5 shadow-sm flex-1">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
            <Mail size={14} className="text-indigo-600" />
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            <span className="font-semibold text-slate-700">Notifications</span> — Les paramètres email se gèrent dans Paramètres → Notifications.
            Les notifications internes restent toujours actives.
          </p>
        </div>

        <button
          onClick={handleSaveProfile}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50 shrink-0"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Enregistrement…' : 'Sauvegarder'}
        </button>
      </div>

    </div>
  );
}
